import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { tap, map, catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ProductBasketActions } from './product-basket.actions';
import { MessageService } from 'primeng/api';
import { ProductBasketClient, AddBasketProductRequest, UpdateBasketProductRequest } from '@api/api-service';
import { AuthUserActions } from '@store/store-auth-user/auth-user.actions';

@Injectable()
export class ProductBasketEffects {
  private actions$ = inject(Actions);
  private messageService = inject(MessageService);
  private productBasketClient = inject(ProductBasketClient);

  getPunchOutFormData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductBasketActions.getPunchoutFormData),
      switchMap(() => {
        return this.productBasketClient.getFormDataForPunchOut().pipe(
          map((response) => ProductBasketActions.getPunchoutFormDataSuccess({ punchOutFormData: response })),
          catchError((error) => of(ProductBasketActions.getPunchoutFormDataFailure({ error: error.message || 'Punchout failed' })))
        );
      })
    )
  );

  punchOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductBasketActions.getPunchoutFormDataSuccess),
      tap((response) => {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = response.punchOutFormData.postUrl;
        form.acceptCharset = 'UTF-8';
        const fields = {
          'return_object_spec_id': response.punchOutFormData.returnObjectSpecId,
          'return_object_mime': response.punchOutFormData.returnObjectMime,
          'return_object_encoding': response.punchOutFormData.returnObjectEncoding,
          'return_object_base64': response.punchOutFormData.returnObjectBase64,
        };

        Object.entries(fields).forEach(([name, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
      })
    ),
    { dispatch: false }
  );

  getBasketOnSessionId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthUserActions.initializePublicAuthenticationSuccess),
      switchMap(() => {
        return this.productBasketClient.getBasketBySessionId().pipe(
          map((response) => {
            return ProductBasketActions.getSessionIDBasketSuccess({ productBasket: response });
          }),
          catchError((error) => of(ProductBasketActions.getSessionIDBasketFailure({ error: error.message || 'Failed to get session ID' })))
        );
      })
    )
  );

  addBasketProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductBasketActions.addBasketProduct),
      switchMap(({ basketProduct }) => {
        const request: AddBasketProductRequest = {
          productId: basketProduct.productId,
          quantity: basketProduct.quantity
        };

        return this.productBasketClient.addProductToBasket(request).pipe(
          map((response) => {
            return ProductBasketActions.addBasketProductSuccess({ productBasket: response });
          }),
          catchError((error) => of(ProductBasketActions.addBasketProductFailure({ error: error.message || 'Failed to add basket product' })))
        );
      })
    )
  );

  updateBasketProductQuantity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductBasketActions.updateBasketProductQuantity),
      switchMap(({ basketProductId, quantity }) => {
        const request: UpdateBasketProductRequest = {
          quantity
        };

        return this.productBasketClient.updateBasketProduct(basketProductId, request).pipe(
          map((response) => ProductBasketActions.updateBasketProductQuantitySuccess({ basketProduct: response })),
          catchError((error) => of(ProductBasketActions.updateBasketProductQuantityFailure({ error: error.message || 'Failed to update basket product quantity' })))
        );
      })
    )
  );

  removeBasketProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductBasketActions.removeBasketProduct),
      switchMap(({ basketProductId }) => {
        return this.productBasketClient.removeProductFromBasket(basketProductId).pipe(
          map(() => ProductBasketActions.removeBasketProductSuccess({ basketProductId })),
          catchError((error) => of(ProductBasketActions.removeBasketProductFailure({ error: error.message || 'Failed to remove basket product' })))
        );
      })
    )
  );

  MESSAGE_SERVICE_AddBasketProductSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductBasketActions.addBasketProductSuccess),
      tap(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Tillagd',
          detail: 'Produkt har lagts till i varukorgen.'
        });
      })
    ),
    { dispatch: false }
  );

  MESSAGE_SERVICE_AddBasketProductFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductBasketActions.addBasketProductFailure),
      tap(({ error }) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Misslyckades',
          detail: error || 'Kunde inte lägga till produkt i varukorgen.'
        });
      })
    ),
    { dispatch: false }
  );

  MESSAGE_SERVICE_UpdateBasketProductQuantitySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductBasketActions.updateBasketProductQuantitySuccess),
      tap(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Uppdaterad',
          detail: 'Antalet har uppdaterats.'
        });
      })
    ),
    { dispatch: false }
  );

  MESSAGE_SERVICE_UpdateBasketProductQuantityFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductBasketActions.updateBasketProductQuantityFailure),
      tap(({ error }) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Misslyckades',
          detail: error || 'Kunde inte uppdatera antalet.'
        });
      })
    ),
    { dispatch: false }
  );

  MESSAGE_SERVICE_RemoveBasketProductSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductBasketActions.removeBasketProductSuccess),
      tap(() => {
        this.messageService.add({
          severity: 'info',
          summary: 'Borttagen',
          detail: 'Produkt har tagits bort från varukorgen.'
        });
      })
    ),
    { dispatch: false }
  );

  MESSAGE_SERVICE_RemoveBasketProductFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductBasketActions.removeBasketProductFailure),
      tap(({ error }) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Misslyckades',
          detail: error || 'Kunde inte ta bort produkt från varukorgen.'
        });
      })
    ),
    { dispatch: false }
  );

  MESSAGE_SERVICE_PunchoutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductBasketActions.getPunchoutFormDataSuccess),
      tap(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Punchout lyckades',
          detail: 'Din varukorg har skickats till punchout-systemet.'
        });
      })
    ),
    { dispatch: false }
  );

  MESSAGE_SERVICE_PunchoutFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductBasketActions.getPunchoutFormDataFailure),
      tap(({ error }) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Punchout misslyckades',
          detail: error || 'Ett fel uppstod vid punchout.'
        });
      })
    ),
    { dispatch: false }
  );
}
